import { useTranslation } from 'react-i18next';
import QrCode from '@/components/ui/QrCode';
import '../loyalty.css';
import { isolate } from '../format';
import type { LoyaltyCard } from '../loyaltyApi';
import CopyableCode from './CopyableCode';

interface Props {
  restaurantName?: string;
  logoUrl?: string | null;
  /** The customer's first name, when known. */
  firstName?: string;
  balance: number;
  /** Undefined when the card endpoint is unavailable — the balance still shows. */
  card?: LoyaltyCard;
  /** At least one reward of the catalog is affordable right now. */
  canUnlock: boolean;
  /** Tap on the QR: the page opens it full screen. */
  onExpandQr: () => void;
}

/**
 * The member pass (SC-1091): a brand-coloured face (restaurant, member, balance,
 * how to earn) and, below a perforation, the white stub staff scan — same
 * vocabulary as the coupon stubs of « Mes avantages ». The progress toward the
 * next reward closes it.
 */
export default function MemberCard({
  restaurantName,
  logoUrl,
  firstName,
  balance,
  card,
  canUnlock,
  onExpandQr,
}: Props) {
  const { t, i18n } = useTranslation();
  const next = card?.next_reward ?? null;
  const pct = next && next.points_cost > 0 ? Math.min(100, Math.max(0, (balance / next.points_cost) * 100)) : 0;
  const perEuro = card?.points_per_euro;
  const perVisit = card?.points_per_visit;
  const showEarn = perEuro !== undefined && (perEuro > 0 || (perVisit ?? 0) > 0);

  return (
    <section
      data-testid="loyalty-card"
      aria-label={t('loyalty.card.title')}
      className="mx-auto w-full max-w-[420px] overflow-hidden rounded-btn border-2 border-solid border-ink bg-paper shadow-stamp"
    >
      <div className="bg-mint px-4 pb-5 pt-4 text-[var(--on-primary)]">
        <div className="flex items-center gap-3">
          {logoUrl && (
            <img
              src={logoUrl}
              alt=""
              className="h-9 w-9 flex-none rounded-full border-2 border-solid border-ink bg-white object-cover"
            />
          )}
          <p className="m-0 min-w-0 flex-1 truncate font-display text-lg leading-tight">{restaurantName}</p>
        </div>

        {firstName && (
          <p data-testid="loyalty-member" className="m-0 mt-4 font-display text-xl leading-tight">
            {firstName}
          </p>
        )}
        <p className={`m-0 font-mono text-xs uppercase tracking-wide opacity-80 ${firstName ? 'mt-3' : 'mt-4'}`}>
          {t('loyalty.balanceLabel')}
        </p>
        <p data-testid="loyalty-balance" className="m-0 mt-1 font-display leading-none">
          <span className="text-6xl">{balance}</span>
          {/* A real space: a screen reader says « 245 pts », not « 245pts ». */}{' '}
          <span className="text-xl">{t('loyalty.card.pointsWord')}</span>
        </p>
        {showEarn && (
          <p data-testid="loyalty-earn" className="m-0 mt-3 font-mono text-xs leading-snug opacity-90">
            {t('loyalty.card.earn', { n: perEuro })}
            {(perVisit ?? 0) > 0 ? ` ${t('loyalty.card.earnVisit', { n: perVisit })}` : ''}
          </p>
        )}
      </div>

      {/* The perforation: the stub below is what the counter scans. */}
      <div aria-hidden="true" className="relative h-0 border-0 border-t-2 border-dashed border-ink">
        <span className="absolute -start-3 top-[-13px] h-6 w-6 rounded-full border-2 border-solid border-ink bg-cream" />
        <span className="absolute -end-3 top-[-13px] h-6 w-6 rounded-full border-2 border-solid border-ink bg-cream" />
      </div>

      <div className="px-4 pb-5 pt-6 text-center">
        {card?.card_code && card.qr_payload ? (
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={onExpandQr}
              aria-label={t('loyalty.card.qrExpand')}
              className="rounded-btn-sm border-2 border-solid border-ink bg-white p-2"
            >
              <QrCode
                payload={card.qr_payload}
                size={264}
                label={t('loyalty.card.qrLabel')}
                className="block h-auto w-[264px] max-w-full"
              />
            </button>
            <p className="m-0 mt-2 font-mono text-xs text-ink-mute">{t('loyalty.card.qrTap')}</p>
            <p className="m-0 mt-3 font-mono text-xs uppercase tracking-wide text-ink-mute">
              {t('loyalty.card.codeLabel')}
            </p>
            <CopyableCode code={card.card_code} codeClassName="text-2xl text-ink" />
            <p className="m-0 mt-1 max-w-[30ch] text-sm text-ink-mute">{t('loyalty.card.showAtCounter')}</p>
          </div>
        ) : card ? (
          <p
            data-testid="loyalty-card-inactive"
            className="m-0 rounded-btn-sm border-2 border-dashed border-[var(--ink-faint)] px-4 py-6 text-sm text-ink-mute"
          >
            {t('loyalty.card.notActivated')}
          </p>
        ) : null}
      </div>

      {next ? (
        <footer className="border-0 border-t-2 border-solid border-ink bg-cream px-4 py-3">
          <p className="m-0 text-sm text-ink">
            {t('loyalty.card.nextReward', { missing: next.missing, name: isolate(next.name, i18n.language) })}
          </p>
          <div
            role="progressbar"
            aria-label={t('loyalty.card.progressLabel')}
            aria-valuemin={0}
            aria-valuemax={next.points_cost}
            aria-valuenow={Math.min(balance, next.points_cost)}
            className="mt-2 h-3 overflow-hidden rounded-full border-2 border-solid border-ink bg-paper"
          >
            <div className="h-full bg-mint" style={{ width: `${pct}%` }} />
          </div>
        </footer>
      ) : canUnlock ? (
        <footer className="border-0 border-t-2 border-solid border-ink bg-cream px-4 py-3">
          <p className="m-0 text-sm text-ink">
            <span aria-hidden="true">🎉 </span>
            {t('loyalty.card.canUnlock')}
          </p>
        </footer>
      ) : null}
    </section>
  );
}
